import * as store from './store.js';
import * as env from './env.js';
import { taskInterfaceFn } from '../interface/TaskInterface.js';
import { TASK_API_ERRORS, TASK_DEFAULT_POLL_TIME, TASK_MINIMUM_POLL_TIME } from './constants.js';

/**
 * Initialize the Tasks API service by detecting environment and setting up TaskServiceInterface,
 * then keep the 'tasks' store filled with the current user's tasks.
 * Mirrors the notification setup pattern from notification-helpers.js
 */
export const setupTasks = () => {
	// Detect environment name using IDM gateway URL or passport domain
	let envName = 'prod';
	const passportData = store.get('passport-data');
	const passportCfg = store.get('passport');
	if (passportCfg === null) { return; }
	if (passportData !== null) {
		envName = env.environmentFromPassportDomain(window.location.hostname);
	}

	const envValues = env[envName] || env.prod;
	const skConfig = store.get('sk-config') || {};
	const taskConfig = skConfig.taskConfig || {};
	// the host-provided base URL wins over the environment default
	const taskSvcBaseUrl = taskConfig.taskBaseUrl || envValues.taskSvcBaseUrl;

	// Guard: if endpoint not configured, log error and return
	if (!taskSvcBaseUrl) {
		console.error(TASK_API_ERRORS.ENDPOINT_REQUIRED);
		return;
	}

	// Initialize TaskServiceInterface
	taskInterfaceFn(store, taskSvcBaseUrl);
	startTaskPolling(passportData, taskConfig.pollingInterval);
};

function getUserId(passportData) {
	const user = passportData && passportData.legacyFormattedData && passportData.legacyFormattedData.user;
	return user && user.profileId != null ? String(user.profileId) : null;
}

let pollTimer = null;

function startTaskPolling(passportData, pollingInterval) {
	const userId = getUserId(passportData);
	const taskApi = window.TaskServiceInterface;
	if (!userId || !taskApi || typeof taskApi.getTasks !== 'function') { return; }

	// a repeated setup must not leave a second polling chain running
	if (pollTimer !== null) { clearTimeout(pollTimer); pollTimer = null; }

	const interval = pollingInterval >= TASK_MINIMUM_POLL_TIME ? pollingInterval : TASK_DEFAULT_POLL_TIME;
	let loaded = false;
	let inFlightSince = 0;

	// only tasks assigned to the current user are shown in the panel
	const fetchTasks = () => {
		const before = store.get('tasks');
		inFlightSince = Date.now();
		return taskApi.getTasks({ assignee: userId })
			.then((result) => {
				// the interface changed the list (create/update/delete) while we waited: that is newer
				if (store.get('tasks') !== before) { return; }
				if (result && result.success) {
					loaded = true;
					store.set('tasks', result.data || []);
				} else if (!loaded) {
					// once tasks were loaded, a transient failure keeps the last good list
					store.set('tasks', { error: true });
				}
			})
			.catch(() => {
				if (!loaded && store.get('tasks') === before) { store.set('tasks', { error: true }); }
			})
			.then(() => { inFlightSince = 0; });
	};

	const poll = () => {
		// schedule first so a hung request cannot stop the polling
		pollTimer = setTimeout(poll, interval);
		// skip while a recent request is pending; one older than two intervals is treated as hung
		if (inFlightSince && Date.now() - inFlightSince < interval * 2) { return; }
		fetchTasks();
	};
	poll();
}
